import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, setSessionToken, useQuery } from '../api.js';
import { useSession, useTheme } from '../session.js';

/**
 * The way back into an account nobody can sign in to.
 *
 * One screen doing two jobs, because they are two halves of the same errand and
 * the link in the email has to land somewhere: with no token it asks for the
 * address to send one to, and with a token it asks for the new password. A
 * token that has expired says so before asking for anything, rather than
 * collecting a password and then refusing it.
 */
export function ResetPassword(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [theme, toggleTheme] = useTheme();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <header className="topbar">
        <Link to="/" className="topbar__brand" style={{ color: '#fff', textDecoration: 'none' }}>
          <span className="topbar__mark" />
          FlightSlots
        </Link>
        <button className="chip" onClick={toggleTheme}>{theme === 'dark' ? 'Light' : 'Dark'}</button>
      </header>

      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          {token ? <ChooseNew token={token} /> : <AskForLink />}
        </div>
      </div>
    </div>
  );
}

/** Step one: send me a link. */
function AskForLink(): JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // The answer is the same whether or not that address has an account, so
      // there is nothing here to read a roster off.
      const { message } = await api.post<{ message: string }>('/password/forgot', { email: email.trim() });
      setSent(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card__head"><span className="card__title">Reset your password</span></div>
      {sent ? (
        <div className="card__body stack">
          <p className="small" style={{ margin: 0, textWrap: 'pretty' }}>{sent}</p>
          <Link className="btn btn--block" to="/login">Back to sign in</Link>
        </div>
      ) : (
        <form className="card__body stack" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
            Give the address you sign in with and we will send a link that is good
            for one hour. If your school does not send mail, your chief instructor
            can set a temporary password for you from the school setup screen.
          </p>
          <label className="field">
            <span>Email</span>
            <input className="input" type="email" autoComplete="username" value={email}
              onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </label>
          {error && <Problem message={error} />}
          <button className="btn btn--primary btn--block" type="submit" disabled={busy || !email.trim()}>
            {busy ? 'Sending…' : 'Send me a link'}
          </button>
          <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>
            Remembered it? <Link to="/login">Sign in</Link>.
          </p>
        </form>
      )}
    </div>
  );
}

/** Step two: the link was opened, so choose the new password. */
function ChooseNew({ token }: { token: string }): JSX.Element {
  const { refresh } = useSession();
  const check = useQuery<{ valid: boolean }>(`/password/reset?token=${encodeURIComponent(token)}`);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { token: session } = await api.post<{ token?: string }>('/password/reset', { token, password: pw });
      // Signed in on the new password immediately: the person came here to get
      // back in, and asking them to type it again is a second chance to fail.
      setSessionToken(session);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set that password');
      setBusy(false);
    }
  };

  if (check.loading) return <div className="card"><div className="card__body small muted">Checking that link…</div></div>;

  if (!check.data?.valid) {
    return (
      <div className="card">
        <div className="card__head"><span className="card__title">That link has expired</span></div>
        <div className="card__body stack">
          <p className="small muted" style={{ margin: 0, textWrap: 'pretty' }}>
            A reset link is good for one hour and can only be used once. Ask for a
            fresh one and it will arrive in the same mailbox.
          </p>
          <Link className="btn btn--primary btn--block" to="/reset">Send another link</Link>
        </div>
      </div>
    );
  }

  const short = pw.length > 0 && pw.length < 10;
  const mismatch = confirm.length > 0 && confirm !== pw;
  const ready = pw.length >= 10 && confirm === pw;

  return (
    <div className="card">
      <div className="card__head"><span className="card__title">Choose a new password</span></div>
      <form className="card__body stack" onSubmit={(e) => { e.preventDefault(); if (ready) submit(); }}>
        <label className="field">
          <span>New password</span>
          <input className="input" type="password" autoComplete="new-password" value={pw}
            onChange={(e) => setPw(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          <span>Type it again</span>
          <input className="input" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} required />
        </label>
        <p className="small muted" style={{ margin: 0 }}>
          {short ? `At least 10 characters — ${10 - pw.length} more.`
            : mismatch ? 'Those two do not match.'
            : 'At least 10 characters. Everywhere you were signed in will be signed out.'}
        </p>
        {error && <Problem message={error} />}
        <button className="btn btn--primary btn--block" type="submit" disabled={busy || !ready}>
          {busy ? 'Setting…' : 'Set password and sign in'}
        </button>
      </form>
    </div>
  );
}

function Problem({ message }: { message: string }): JSX.Element {
  return (
    <div style={{
      padding: 8, background: 'var(--conflict-bg)', border: '1px solid var(--conflict-bd)',
      color: 'var(--conflict-c)', borderRadius: 6, fontSize: 12.5,
    }}>
      {message}
    </div>
  );
}
